import type { DBSchema, IDBPDatabase, OpenDBCallbacks } from 'idb';
import { openDB, wrap } from 'idb';

import { atlIndexedDb } from '@atlassian/browser-storage-controls/indexed-db-wrapper-service';

import { fg } from '@confluence/feature-gating';

/**
 * UNSAFE: Opens a Privacy-approved Strictly Necessary IndexedDB database using
 * the existing idb behavior or AtlIndexedDb.
 *
 * Do not use this helper without explicit Strictly Necessary approval from
 * #help-cookies and a corresponding registration in atlassian-cookies. When the
 * migration path is enabled, this bypasses Browser Storage Controls consent and
 * category checks and does not wait for external category-cache loaders.
 *
 * When IndexedDB is available, AtlIndexedDb.openStrictlyNecessary() returns the native
 * IDBOpenDBRequest from its configured IDBFactory. This adapter wraps the request's
 * native database and transaction results to preserve idb's promise and callback
 * contract while routing the underlying open through AtlIndexedDb when the migration
 * gate is enabled.
 */
export function UNSAFE_openStrictlyNecessaryIndexedDb<DBTypes extends DBSchema | unknown = unknown>(
	name: string,
	version?: number,
	{ blocked, upgrade, blocking, terminated }: OpenDBCallbacks<DBTypes> = {},
): Promise<IDBPDatabase<DBTypes>> {
	if (!fg('confluence_atlindexeddb_migration_enabled')) {
		return openDB<DBTypes>(name, version, { blocked, upgrade, blocking, terminated });
	}

	const request = atlIndexedDb.openStrictlyNecessary(name, version);
	const openPromise = new Promise<IDBPDatabase<DBTypes>>((resolve, reject) => {
		request.addEventListener('success', () => {
			resolve(wrap(request.result) as IDBPDatabase<DBTypes>);
		});
		request.addEventListener('error', () => {
			reject(request.error ?? new Error(`Unable to open IndexedDB database "${name}"`));
		});
	});

	if (upgrade) {
		request.addEventListener('upgradeneeded', (event) => {
			const versionChangeEvent = event as IDBVersionChangeEvent;
			upgrade(
				wrap(request.result) as IDBPDatabase<DBTypes>,
				versionChangeEvent.oldVersion,
				versionChangeEvent.newVersion,
				wrap(request.transaction!) as unknown as Parameters<
					NonNullable<OpenDBCallbacks<DBTypes>['upgrade']>
				>[3],
				versionChangeEvent,
			);
		});
	}

	if (blocked) {
		request.addEventListener('blocked', (event) => {
			const versionChangeEvent = event as IDBVersionChangeEvent;
			blocked(versionChangeEvent.oldVersion, versionChangeEvent.newVersion, versionChangeEvent);
		});
	}

	void openPromise
		.then((database) => {
			if (terminated) {
				database.addEventListener('close', terminated);
			}
			if (blocking) {
				database.addEventListener('versionchange', (event) => {
					const versionChangeEvent = event as IDBVersionChangeEvent;
					blocking(
						versionChangeEvent.oldVersion,
						versionChangeEvent.newVersion,
						versionChangeEvent,
					);
				});
			}
		})
		.catch(() => {});

	return openPromise;
}

export function getIndexedDbDatabases(): Promise<IDBDatabaseInfo[]> {
	if (fg('confluence_atlindexeddb_migration_enabled')) {
		return atlIndexedDb.databases();
	}

	// oxlint-disable-next-line no-restricted-properties -- Temporary native fallback while the AtlIndexedDb migration gate remains.
	return window.indexedDB?.databases?.() ?? Promise.resolve([]);
}

/**
 * UNSAFE: Returns an open request that bypasses consent and category checks when
 * AtlIndexedDb is enabled. Do not use without explicit Strictly Necessary
 * approval from #help-privacy and registration in atlassian-cookies.
 */
export function UNSAFE_openStrictlyNecessaryIndexedDbRequest(
	databaseName: string,
): IDBOpenDBRequest {
	return fg('confluence_atlindexeddb_migration_enabled')
		? atlIndexedDb.openStrictlyNecessary(databaseName)
		: // oxlint-disable-next-line no-restricted-properties -- Temporary native fallback while the AtlIndexedDb migration gate remains.
			window.indexedDB.open(databaseName);
}

export function deleteIndexedDbDatabase(databaseName: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = fg('confluence_atlindexeddb_migration_enabled')
			? atlIndexedDb.deleteDatabase(databaseName)
			: // oxlint-disable-next-line no-restricted-properties -- Temporary native fallback while the AtlIndexedDb migration gate remains.
				window.indexedDB.deleteDatabase(databaseName);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		request.onblocked = () => resolve();
	});
}
